import React from 'react';

/**
 * The shared media control — one of the four corner components of a card
 * (Media, Elements, Settings, Layout: round trigger, blue when open, one
 * open at a time). It knows nothing about originals or tends: the card's
 * adapter supplies the pool position, what the buttons do, and the corner.
 *
 *   trigger ▸ [preview] [delete] [add] [‹] [n/N] [›]      anchor bottom-left, opens right
 *   [preview] [delete] [add] [‹] [n/N] [›] ◂ trigger      anchor top-right, opens left
 *
 * Round buttons throughout (the family's recipe), 4 px gaps, a 28 px
 * preview thumb (video-aware). The preview becomes the Gallery opener in
 * the next ticket.
 */

export type MediaControlAnchor = 'bottom-left' | 'top-right';

export interface MediaControlProps {
  anchor: MediaControlAnchor;
  /** Open state — owned by the card's corner coordinator. */
  isOpen: boolean;
  onToggle: () => void;
  /** The card's hover state: reveals the trigger on desktop. Phones always show it. */
  visible: boolean;
  /** The selected item's preview (resolved URL) and whether it is a video. */
  previewUrl?: string;
  previewIsVideo?: boolean;
  /** 1-based position and total; total 0 = empty pool. */
  position: number;
  total: number;
  canDelete: boolean;
  deleteTitle: string;
  onDelete: (e: React.MouseEvent) => void;
  onAdd: () => void;
  onPrev: () => void;
  onNext: () => void;
  /** Shows a spinner in the pill and disables the mutating buttons. */
  busy?: boolean;
  /** Inline message (short); long refusals belong in a modal. */
  error?: string | null;
  /** Swipe inside the pill (optional). */
  onPointerDownCapture?: (clientX: number) => void;
  onPointerUpCapture?: (clientX: number) => void;
  /** Hook for tests / the Gallery ticket: click on the preview. */
  onPreviewClick?: () => void;
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

const ROUND = 'p-1.5 rounded-full text-white transition-colors';

export const MediaControl: React.FC<MediaControlProps> = ({
  anchor, isOpen, onToggle, visible, previewUrl, previewIsVideo, position, total,
  canDelete, deleteTitle, onDelete, onAdd, onPrev, onNext, busy = false, error,
  onPointerDownCapture, onPointerUpCapture, onPreviewClick,
}) => {
  const opensLeft = anchor === 'top-right';
  const corner = opensLeft ? 'top-2 right-2' : 'bottom-2 left-2';
  const shown = visible || isOpen || busy || !!error;
  const hasMany = total > 1;

  return (
    <div
      data-testid="media-control"
      data-anchor={anchor}
      data-open={isOpen ? 'true' : 'false'}
      className={`absolute ${corner} z-40 flex items-center gap-1 transition-all duration-200 ${opensLeft ? 'flex-row-reverse' : 'flex-row'}
        ${shown ? 'opacity-100 translate-x-0' : `md:opacity-0 ${opensLeft ? 'md:translate-x-2' : 'md:-translate-x-2'} md:pointer-events-none`}`}
      onClick={stop}
      onMouseDown={stop}
      onTouchStart={stop}
    >
      {/* Trigger — the family's round puck, blue when open */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className={`p-2 rounded-full text-white transition-colors shadow-lg ${isOpen ? 'bg-blue-600' : 'bg-black/60 hover:bg-blue-600'}`}
        title={isOpen ? 'Close media' : 'Media'}
        aria-expanded={isOpen}
        aria-label="Media"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      {/* The pill is h-8 = the trigger's height (p-2 + 16 px icon): the row
          never grows when it opens, so the trigger stays put in its corner. */}
      {isOpen && (
        <div
          data-testid="media-control-pill"
          className="flex items-center gap-1 h-8 bg-black/60 backdrop-blur-sm rounded-full px-1.5 touch-pan-y select-none"
          onMouseDown={(e) => { e.stopPropagation(); onPointerDownCapture?.(e.clientX); }}
          onMouseUp={(e) => { e.stopPropagation(); onPointerUpCapture?.(e.clientX); }}
          onTouchStart={(e) => { e.stopPropagation(); onPointerDownCapture?.(e.touches[0].clientX); }}
          onTouchEnd={(e) => { e.stopPropagation(); onPointerUpCapture?.(e.changedTouches[0].clientX); }}
        >
          {/* 1. Preview (→ Gallery next) */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPreviewClick?.(); }}
            className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0 border border-white/30 bg-black/40"
            title={total ? `Media ${position} of ${total}` : 'No media yet'}
            aria-label="Preview"
            data-testid="media-control-preview"
          >
            {previewUrl ? (
              previewIsVideo ? (
                <video src={previewUrl} className="w-full h-full object-cover" muted playsInline />
              ) : (
                <img src={previewUrl} alt="" className="w-full h-full object-cover" />
              )
            ) : null}
          </button>

          {/* 2. Delete */}
          <button
            type="button"
            onClick={onDelete}
            disabled={!canDelete || busy}
            className={`${ROUND} ${canDelete ? 'hover:bg-red-600' : 'opacity-40 cursor-not-allowed'}`}
            title={canDelete ? deleteTitle : "Can't delete the last item"}
            aria-label="Delete"
            data-testid="media-control-delete"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>

          {/* 3. Add */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onAdd(); }}
            disabled={busy}
            className={`${ROUND} hover:bg-green-600`}
            title="Add media"
            aria-label="Add media"
            data-testid="media-control-add"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>

          {/* 4–6. ‹ n/N › */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            disabled={!hasMany}
            className={`${ROUND} ${hasMany ? 'hover:bg-white/20' : 'opacity-40 cursor-not-allowed'}`}
            title="Previous media"
            aria-label="Previous media"
            data-testid="media-control-prev"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-white text-[11px] font-medium tabular-nums whitespace-nowrap px-0.5" data-testid="media-control-count">
            {total ? `${position}/${total}` : '0/0'}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            disabled={!hasMany}
            className={`${ROUND} ${hasMany ? 'hover:bg-white/20' : 'opacity-40 cursor-not-allowed'}`}
            title="Next media"
            aria-label="Next media"
            data-testid="media-control-next"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {busy && (
            <svg className="w-3.5 h-3.5 animate-spin text-white/70" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
        </div>
      )}

      {/* Inline error — short messages only (upload/add failures); refusals go to a modal */}
      {error && (
        <span className="text-red-400 text-xs bg-black/70 rounded px-2 py-1 max-w-[180px] truncate" title={error}>
          {error}
        </span>
      )}
    </div>
  );
};

export default MediaControl;
